import React from 'react';
import { GitCompare, X } from 'lucide-react';
import { useCompareTray } from '../../context/CompareTrayContext';
import { KIND_LABEL } from '../../data/researchCatalog';
import CompareModal from './CompareModal';

/**
 * CompareTrayBar - floating bar (bottom-centre) that appears whenever the
 * compare tray has items. Shows the current picks + "Compare" button.
 * Mounted once globally in App.js.
 */
const CompareTrayBar = () => {
  const { items, count, kind, remove, clear, toast, open, setOpen } = useCompareTray();

  // Keep the tray clear of the cookie-consent banner (also fixed bottom,
  // z-100) on first visits so its "Compare" button is always clickable.
  let cookieChoice = null;
  try { cookieChoice = (typeof window !== 'undefined') && window.localStorage.getItem('buildpilot_cookie_consent'); } catch { /* ignore */ }
  const bottomClass = cookieChoice ? 'bottom-4' : 'bottom-28 sm:bottom-24';

  return (
    <>
      {/* Toast */}
      {toast && (
        <div
          className="fixed bottom-40 left-1/2 -translate-x-1/2 z-[115] bg-[#1d1d1f] text-white text-[13px] px-4 py-2 rounded-full shadow-lg"
          data-testid="compare-tray-toast"
        >
          {toast}
        </div>
      )}

      {count > 0 && (
        <div
          className={`fixed ${bottomClass} left-1/2 -translate-x-1/2 z-[101] w-[calc(100%-1.5rem)] max-w-2xl`}
          data-testid="compare-tray-bar"
        >
          <div className="bg-white border border-[#e8e8ed] shadow-[0_12px_40px_rgba(0,0,0,0.18)] rounded-2xl px-3 py-2.5 flex items-center gap-3">
            <div className="flex items-center gap-1.5 flex-1 min-w-0 overflow-x-auto">
              <span className="text-[12px] font-semibold text-[#86868b] whitespace-nowrap mr-1">
                Comparing {count}/{4}
              </span>
              {items.map((it) => (
                <span
                  key={it.__id}
                  className="inline-flex items-center gap-1 bg-[#f5f5f7] text-[#1d1d1f] text-[11.5px] font-medium px-2 py-1 rounded-full whitespace-nowrap"
                >
                  <span className="max-w-[110px] truncate">{it.__label || 'Item'}</span>
                  <button
                    type="button"
                    onClick={() => remove(it.__id)}
                    aria-label="Remove"
                    data-testid={`compare-tray-remove-${it.__id}`}
                    className="text-[#86868b] hover:text-[#1d1d1f]"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <button
              type="button"
              onClick={clear}
              className="text-[12px] text-[#86868b] hover:text-[#1d1d1f] whitespace-nowrap hidden sm:inline"
              data-testid="compare-tray-clear"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setOpen(true)}
              disabled={count < 2}
              data-testid="compare-tray-open"
              className={`inline-flex items-center gap-1.5 text-[13px] font-semibold px-4 py-2 rounded-full whitespace-nowrap transition-colors ${
                count < 2 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-[#1d8efe] text-white hover:bg-[#d9842b]'
              }`}
            >
              <GitCompare className="w-4 h-4" />
              Compare
            </button>
          </div>
          {count < 2 && (
            <p className="text-center text-[11px] text-[#86868b] mt-1.5">
              Add one more {KIND_LABEL[kind] ? KIND_LABEL[kind].replace(/s$/, '') : 'item'} to compare
            </p>
          )}
        </div>
      )}

      {open && <CompareModal />}
    </>
  );
};

export default CompareTrayBar;
